"use client";

import { useEffect, useState } from "react";
import { onSnapshot, query, orderBy } from "firebase/firestore";
import { getDb } from "@/lib/firebase/client";
import { paths, refCol } from "@/lib/db/paths";
import { choreSchema, type Chore } from "@/lib/schemas";
import { markReady } from "@/lib/realtime/registry";

type State = { chores: Chore[]; loading: boolean; error: string | null };

// Module-level cache — pamiętany między re-mountami komponentów (np. po
// zmianie tab w bottom nav). Pierwszy mount: loading=true. Kolejne: dane
// z cache od razu, brak migotania "Ładowanie...".
const cache = new Map<string, Chore[]>();
export const choresKey = (hid: string) => `chores:${hid}`;

export function useChores(householdId: string | undefined): State {
  const cached = householdId ? cache.get(householdId) : undefined;
  const [state, setState] = useState<State>({
    chores: cached ?? [],
    loading: cached === undefined,
    error: null,
  });

  useEffect(() => {
    if (!householdId) return;
    const db = getDb();
    const q = query(refCol(db, paths.chores(householdId)), orderBy("title"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const chores = snap.docs.flatMap((d) => {
          const parsed = choreSchema.safeParse({ id: d.id, ...d.data() });
          return parsed.success ? [parsed.data] : [];
        });
        cache.set(householdId, chores);
        markReady(choresKey(householdId));
        setState({ chores, loading: false, error: null });
      },
      (err) => setState({ chores: [], loading: false, error: err.message }),
    );
    return () => unsub();
  }, [householdId]);

  return state;
}
